import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useLogin } from '@/hooks/auth';
import { useLocale, useTranslations } from '@/i18n';
import { consumeAuthRedirectInfo, normalizePostLoginRedirectPath } from '@/lib/auth/auth-session';
import { getAccessToken } from '@/lib/auth/token-storage';

function extractRedirectPathFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object' || !('from' in state)) {
    return null;
  }

  const from = (state as { from?: unknown }).from;

  if (typeof from === 'string') {
    return normalizePostLoginRedirectPath(from);
  }

  if (!from || typeof from !== 'object') {
    return null;
  }

  const pathname = typeof (from as { pathname?: unknown }).pathname === 'string'
    ? (from as { pathname: string }).pathname
    : '';
  const search = typeof (from as { search?: unknown }).search === 'string'
    ? (from as { search: string }).search
    : '';
  const hash = typeof (from as { hash?: unknown }).hash === 'string'
    ? (from as { hash: string }).hash
    : '';

  return normalizePostLoginRedirectPath(`${pathname}${search}${hash}`);
}

function isLoginPath(pathname: string): boolean {
  return pathname === '/login' || pathname.startsWith('/login?') || pathname.startsWith('/login#');
}

export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations('auth.login');
  const { locale } = useLocale();
  const logoSrc = locale === 'en' ? '/assets/logo-en.svg' : '/assets/logo.svg';
  const startSsoLogin = useLogin();
  const [authRedirectInfo] = useState(() => consumeAuthRedirectInfo());
  const [isRedirecting, setIsRedirecting] = useState(false);

  const authErrorMessage = useMemo(() => {
    if (authRedirectInfo?.status !== 'error') {
      return null;
    }

    if (!authRedirectInfo.message || authRedirectInfo.message === 'session-expired') {
      return t('sessionExpired');
    }

    return authRedirectInfo.message;
  }, [authRedirectInfo, t]);

  const postLoginRedirectPath = useMemo(() => {
    const stateRedirectPath = extractRedirectPathFromState(location.state);
    if (stateRedirectPath && !isLoginPath(stateRedirectPath)) {
      return stateRedirectPath;
    }

    const searchParams = new URLSearchParams(location.search);
    const queryRedirectPath = normalizePostLoginRedirectPath(searchParams.get('redirect'));
    if (queryRedirectPath && !isLoginPath(queryRedirectPath)) {
      return queryRedirectPath;
    }

    return '/';
  }, [location.search, location.state]);

  useEffect(() => {
    if (getAccessToken()) {
      navigate(postLoginRedirectPath, { replace: true });
    }
  }, [navigate, postLoginRedirectPath]);

  const handleLoginClick = () => {
    setIsRedirecting(true);
    startSsoLogin(postLoginRedirectPath);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white md:flex-row">
      <div className="flex flex-1 items-center justify-center py-8 md:py-0">
        <div className="flex w-full max-w-[31.625rem] flex-col items-start gap-8 px-6 md:gap-[5.375rem] md:px-0">
          <div className="h-[3.52806rem] w-[11.375rem]">
            <img
              src={logoSrc}
              alt="Cellfie Logo"
              className="h-full w-full object-contain"
            />
          </div>

          <div className="flex flex-col justify-end gap-8 self-stretch px-4 py-0 md:px-4">
            <div className="flex flex-col items-start gap-2 self-stretch">
              <h1 className="self-stretch text-[2rem] leading-[2.5rem] text-black md:text-[2.5rem] md:leading-[3rem]">
                {t('title')}
              </h1>
              <p className="w-full text-[1rem] leading-[1.5rem] text-[#292929] md:w-[24.5rem]">
                {t('description')}
              </p>
            </div>

            {authErrorMessage && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <p className="text-red-800">{authErrorMessage}</p>
              </div>
            )}

            <Button
              onClick={handleLoginClick}
              disabled={isRedirecting}
              variant="brand"
              className="flex h-[3.5rem] w-[11.625rem] flex-shrink-0 flex-grow basis-0 items-center justify-center gap-2 rounded-[12.5rem] px-6 py-4 text-center leading-6 font-medium"
            >
              {isRedirecting && <Spinner variant="circle" size={16} className="text-white" />}
              {t('button')}
            </Button>
          </div>
        </div>
      </div>

      <div className="relative h-[20rem] w-full shrink-0 overflow-hidden px-[1.5rem] py-[2rem] [background:var(--Main-grad,_linear-gradient(180deg,#F16566_-3.82%,#F05E6A_0.3%,#EE3F7F_23.64%,#ED2C8B_42.86%,#ED2690_56.59%,#E42790_64.83%,#CC2B92_78.56%,#A63294_96.41%,#713C98_117.01%,#41459C_133.48%))] md:h-screen md:w-[37.5rem] md:px-[2.75rem] md:py-[2.875rem]">
        <div className="pointer-events-none absolute bottom-[-12.85456rem] left-[-28.5625rem] z-0 hidden h-[57.91706rem] w-[78.875rem] select-none md:block">
          <img
            alt="Background pattern"
            className="h-full w-full"
            src="/assets/login/background-pattern.svg"
          />
        </div>

        <div className="relative z-10 flex h-full flex-col items-start pt-[2.5rem] md:pt-[4.125rem]">
          <div className="flex max-w-[32rem] flex-col gap-3 text-white">
            <h2 className="text-[2rem] leading-[2.5rem] font-medium md:text-[3rem] md:leading-[3.5rem]">
              {t('heading')}
            </h2>
            <p className="max-w-[20.1875rem] text-[1rem] leading-[1.5rem] font-normal md:text-[1.125rem] md:leading-[1.75rem]">
              {t('subheading')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
